import {
  lazy,
  Suspense,
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DEFAULT_ROUTES } from "@/constants/routes";
import { useAuth } from "@/features/authentication";
import { ProposalI18nProvider } from "@/features/vision-360/proposal-standard/i18n/ProposalI18nContext";
import LocationPreview from "../ai-mode/preview/LocationPreview";
import PartnerSearchStep from "./PartnerSearchStep";
import {
  trackPartnerDemoStarted,
  trackPartnerDemoProposalViewed,
  trackPartnerDemoBackToSearch,
} from "./analytics";
import "../ai-mode/ai-mode-wizard.css";

// Lazy so the router's static import of this wizard doesn't pull the whole
// proposal-standard template into the app's main chunk — that chunk got big
// enough to run `vite build` out of heap on CI. Same module the proposal
// route already lazy-loads, so both share one chunk.
const ProposalContent = lazy(async () => {
  const mod = await import(
    "@/features/vision-360/proposal-standard/ProposalPage"
  );
  return { default: mod.ProposalContent };
});
import type {
  LivePreview,
  RankedSpace,
  ProposalPayload,
} from "../ai-mode/types";

type View = "search" | "proposal";

function buildProposalPayload(
  selectedSpaces: RankedSpace[],
  locationLabel: string,
): ProposalPayload {
  return {
    uuid: "partner-demo",
    generatedAt: new Date().toISOString(),
    suggestedSpaces: selectedSpaces,
    state: {
      currentStep: 3,
      completedSteps: [0, 1, 2],
      diagnosis: {
        dealId: "",
        companyName: "Demonstração Parceiro",
        contactName: "",
        contactEmail: "",
        segment: "",
        targetCity: locationLabel,
        positionsMin: "",
        positionsMax: "",
        monthlyBudget: "",
        needs: "",
      },
      location: null,
      adjustments: null,
    },
  };
}

export default function PartnerDemoWizard() {
  const navigate = useNavigate();
  const { userInfo } = useAuth();

  const [view, setView] = useState<View>("search");
  const [selectedSpaces, setSelectedSpaces] = useState<RankedSpace[]>([]);
  const [livePreview, setLivePreview] = useState<LivePreview>({
    companyName: "",
    targetCity: "",
    positionsMin: "",
    positionsMax: "",
    dealImported: false,
    location: null,
    radiusKm: null,
    activeSpaceId: null,
  });
  const [isStepValid, setIsStepValid] = useState(false);
  const stepNextRef = useRef<(() => void) | null>(null);

  const registerNext = useCallback((fn: () => void) => {
    stepNextRef.current = fn;
  }, []);

  const handlePreviewUpdate = useCallback((data: Partial<LivePreview>) => {
    setLivePreview((prev) => ({ ...prev, ...data }));
  }, []);

  useEffect(() => {
    trackPartnerDemoStarted({
      userId: userInfo?.user?.id ?? null,
      email: userInfo?.profile?.email ?? null,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchNext(spaces: RankedSpace[]) {
    setSelectedSpaces(spaces);
    trackPartnerDemoProposalViewed(
      {
        userId: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
      },
      spaces.map((s) => s.id),
      livePreview.location?.label ?? null,
    );
    setView("proposal");
  }

  function handleBack() {
    if (view === "proposal") {
      trackPartnerDemoBackToSearch({
        userId: userInfo?.user?.id ?? null,
        email: userInfo?.profile?.email ?? null,
      });
      setView("search");
    } else {
      navigate(DEFAULT_ROUTES.VISION_360);
    }
  }

  function handleFooterNext() {
    stepNextRef.current?.();
  }

  // Stable across re-renders: the proposal template memoizes on the payload
  // and its `suggestedSpaces` (dedup, combos, providers).
  const locationLabel = livePreview.location?.label ?? "";
  const proposalPayload = useMemo(
    () => buildProposalPayload(selectedSpaces, locationLabel),
    [selectedSpaces, locationLabel],
  );

  if (view === "proposal") {
    return (
      <div
        className="fixed flex flex-col"
        style={{
          top: 64,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10,
          fontFamily: "'Space Grotesk', system-ui, sans-serif",
        }}
      >
        {/* Proposal top bar */}
        <div
          className="flex-none w-full px-6 py-3 bg-white flex items-center justify-between gap-4"
          style={{ borderBottom: "1px solid #E8E6E3", zIndex: 1 }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            className="gap-1.5"
          >
            <ChevronLeft className="w-4 h-4" />
            Voltar à busca
          </Button>
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-semibold px-3 py-1 rounded-full"
              style={{ background: "#FFD900", color: "#1A1814" }}
            >
              Demonstração Parceiro
            </span>
          </div>
          <div style={{ width: 120 }} />
        </div>

        {/* Proposal content. The standard template's root (and its mobile
          full-screen map) is `position: fixed; inset: 0` — built to own the
          whole viewport. The transform makes this box their containing
          block, so the proposal fills the area below the bar above instead
          of covering it (and the app header). It scrolls on its own. */}
        <div
          data-testid="partner-demo-proposal-frame"
          className="relative flex-1 min-h-0 overflow-hidden"
          style={{ transform: "translateZ(0)" }}
        >
          <ProposalI18nProvider>
            <Suspense fallback={null}>
              <ProposalContent
                proposal={proposalPayload}
                email=""
                hidePrice
                isPartnerMode
              />
            </Suspense>
          </ProposalI18nProvider>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed flex flex-col"
      style={{
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10,
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      {/* Top bar */}
      <div
        className="flex-none w-full px-10 py-4 bg-white flex items-center justify-between gap-4"
        style={{ borderBottom: "1px solid #E8E6E3" }}
      >
        <div className="flex items-center gap-3">
          <span className="text-base font-bold" style={{ color: "#1A1814" }}>
            Demonstração Parceiro
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full font-medium"
            style={{ background: "#F0EEEB", color: "#78736D" }}
          >
            Vitrine do Parceiro
          </span>
        </div>
      </div>

      {/* Main split panel */}
      <div className="flex flex-1 overflow-hidden flex-col lg:flex-row">
        {/* Left: preview */}
        <div
          className="relative overflow-hidden flex-none hidden lg:block"
          style={{ width: "60%", background: "#FDFBF7" }}
        >
          <div
            className="w-full h-full"
            style={{
              animation:
                "aiPreviewFade 0.3s cubic-bezier(0.16,1,0.3,1) forwards",
            }}
          >
            <LocationPreview preview={livePreview} />
          </div>
        </div>

        {/* Right: search form */}
        <div
          className="flex flex-col flex-1 lg:flex-none lg:w-[40%] bg-white overflow-hidden"
          style={{ borderLeft: "1px solid #E8E6E3" }}
        >
          {/* Mobile preview */}
          <div
            className="lg:hidden w-full"
            style={{
              background: "#FDFBF7",
              borderBottom: "1px solid #E8E6E3",
              height: "200px",
              flexShrink: 0,
              overflow: "hidden",
            }}
          >
            <LocationPreview preview={livePreview} />
          </div>

          {/* Step content */}
          <div
            className="flex-1 overflow-y-auto px-5 py-4 ai-wizard-scroll"
            style={{
              animation:
                "aiStepSlide 0.35s cubic-bezier(0.16,1,0.3,1) forwards",
            }}
          >
            <PartnerSearchStep
              onNext={handleSearchNext}
              onPreviewUpdate={handlePreviewUpdate}
              onValidityChange={setIsStepValid}
              registerNext={registerNext}
            />
          </div>
        </div>
      </div>

      {/* Footer nav */}
      <div
        className="flex-none w-full flex items-center justify-between px-10 py-4 bg-white"
        style={{ borderTop: "1px solid #E8E6E3" }}
      >
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          className="gap-1.5"
        >
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Button>

        <Button
          type="button"
          onClick={handleFooterNext}
          disabled={!isStepValid}
          className="gap-1.5 px-8"
          style={
            isStepValid
              ? {
                  background: "#FFD900",
                  color: "#1A1814",
                  borderColor: "#FFD900",
                }
              : {}
          }
        >
          <Eye className="w-4 h-4" />
          Ver Proposta
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
